import { useMemo, useState } from 'react';
import { useAdminI18n } from '@/lib/i18n/use-admin-i18n';
import { STATUS_OPTIONS } from '@cms/utils';
import toast from 'react-hot-toast';
import { DynamicTable } from '@/components/custom/table/DynamicTable';
import type {
  DynamicTableColumnConfig,
  DynamicTableControllers,
} from '@/components/custom/table/dynamic-table-types';
import type { DesktopReleaseItem } from '@cms/models';
import {
  desktopReleasesQueryKeys,
  useDesktopReleases,
  useDesktopReleaseDownloadUrl,
  usePublishDesktopRelease,
} from '@/lib/api/desktop-releases.api';
import {
  DesktopReleaseFormModal,
  type DesktopReleaseFormModalMode,
} from './DesktopReleaseFormModal';
import { Button } from '@/components/ui/button';
import {
  AdminTableRowActions,
  PagePrimaryActionButton,
} from '@/components/common/AdminTableRowActions';
import { extractApiErrorMessage } from '@/lib/api/extract-api-error-message';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import type { PaginationState, SortingState } from '@tanstack/react-table';
import { SearchFilter, StatusFilter } from '@/components/custom/filters';

const VEO3_PRO_APP_SLUG = 'veo3-pro';
type DesktopReleaseStatusFilter = (typeof STATUS_OPTIONS)[number]['value'] | '';

function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null || Number.isNaN(bytes)) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let idx = 0;
  let n = bytes;
  while (n >= 1024 && idx < units.length - 1) {
    n /= 1024;
    idx++;
  }
  const rounded = idx === 0 ? n : Math.round(n * 10) / 10;
  return `${rounded} ${units[idx]}`;
}

function DesktopReleaseActionsCell(props: {
  readonly row: DesktopReleaseItem;
  readonly isCopyPending: boolean;
  readonly isPublishPending: boolean;
  readonly onCopy: (row: DesktopReleaseItem) => void;
  readonly onPublish: (releaseId: string | undefined | null) => void;
}) {
  const { row, isCopyPending, isPublishPending, onCopy, onPublish } = props;
  const showPublish = row.publishedAt == null;
  const canCopy = row.appSlug === VEO3_PRO_APP_SLUG;

  return (
    <div className="flex items-center gap-1.5 flex-wrap justify-end">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-7 rounded-md border-blue-500/50 bg-background/70 px-2.5 text-xs font-medium text-blue-600 hover:bg-blue-500/10 dark:text-blue-400 dark:hover:bg-blue-500/20"
        disabled={isCopyPending || !canCopy}
        onClick={() => onCopy(row)}
      >
        Copy link
      </Button>
      {showPublish && (
        <Button
          type="button"
          size="sm"
          className={cn('h-7 px-2 text-xs', isPublishPending ? 'opacity-70' : '')}
          disabled={isPublishPending}
          onClick={() => onPublish(row.id)}
        >
          {isPublishPending ? 'Publishing…' : 'Publish'}
        </Button>
      )}
    </div>
  );
}

function makeDesktopReleaseActionsCellRenderer(props: {
  readonly isCopyPending: boolean;
  readonly isPublishPending: boolean;
  readonly onCopy: (row: DesktopReleaseItem) => void;
  readonly onPublish: (releaseId: string | undefined | null) => void;
}) {
  return ({
    row,
    value: _value,
    index: _index,
  }: {
    row: DesktopReleaseItem;
    value: unknown;
    index: number;
  }) => (
    <DesktopReleaseActionsCell
      row={row}
      isCopyPending={props.isCopyPending}
      isPublishPending={props.isPublishPending}
      onCopy={props.onCopy}
      onPublish={props.onPublish}
    />
  );
}

export function DesktopReleasesPage() {
  const { col, act, page, statusOptions, allStatusesOption } = useAdminI18n();
  const queryClient = useQueryClient();
  const query = useDesktopReleases({ includeDraft: true });
  const downloadUrlMutation = useDesktopReleaseDownloadUrl();
  const publishMutation = usePublishDesktopRelease();

  const [sorting, setSorting] = useState<SortingState>([
    { id: 'publishedAt', desc: true },
  ]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<DesktopReleaseStatusFilter>('');
  const [formModal, setFormModal] = useState<{
    open: boolean;
    mode: DesktopReleaseFormModalMode;
    release: DesktopReleaseItem | null;
  }>({ open: false, mode: 'create', release: null });

  const handleCopy = async (row: DesktopReleaseItem) => {
    try {
      if (!row.id) {
        toast.error('Missing release id');
        return;
      }
      if (row.appSlug !== VEO3_PRO_APP_SLUG) {
        toast.error('Copy link chỉ hỗ trợ cho `veo3-pro` (v1 refresh download URL).');
        return;
      }
      const out = await downloadUrlMutation.mutateAsync({
        releaseId: row.id,
      });
      if (!out?.downloadUrl) throw new Error('Missing downloadUrl');
      await globalThis.navigator.clipboard.writeText(out.downloadUrl);
      const msg = out.expiresInSec > 0 
        ? `Copied link (expires in ${out.expiresInSec}s)` 
        : `Copied link`;
      toast.success(msg);
    } catch (error: unknown) {
      toast.error(extractApiErrorMessage(error));
    }
  };

  const handlePublish = async (releaseId: string | undefined | null) => {
    try {
      if (!releaseId) {
        toast.error('Missing release id');
        return;
      }
      await publishMutation.mutateAsync(releaseId);
      toast.success('Release published');
      await queryClient.invalidateQueries({ queryKey: desktopReleasesQueryKeys.all });
    } catch (error: unknown) {
      toast.error(extractApiErrorMessage(error));
    }
  };

  const columns: DynamicTableColumnConfig<DesktopReleaseItem>[] = useMemo(
    () => [
      {
        id: 'no',
        header: col('no'),
        columnType: 'number',
        cell: ({ index }) => pageState.pageIndex * pageState.pageSize + index + 1,
        widthMode: 'fit',
        size: 64,
        meta: { className: 'w-16 text-muted-foreground' },
      },
      {
        id: 'appSlug',
        accessorKey: 'appSlug',
        header: col('app'),
        columnType: 'text',
        sortable: false,
        widthMode: 'fit',
        size: 110,
        minSize: 90,
        meta: { className: 'min-w-[90px]' },
      },
      {
        id: 'channel',
        accessorKey: 'channel',
        header: col('channel'),
        columnType: 'badge',
        sortable: false,
        displayLabelOptions: [
          { value: 'stable', label: 'stable' },
          { value: 'beta', label: 'beta' },
        ],
        widthMode: 'fit',
        size: 95,
        minSize: 85,
        meta: { className: 'min-w-[85px]' },
      },
      {
        id: 'version',
        accessorKey: 'version',
        header: col('version'),
        columnType: 'text',
        sortable: false,
        widthMode: 'fit',
        size: 95,
        minSize: 85,
        meta: { className: 'min-w-[85px]' },
      },
      {
        id: 'platform',
        accessorKey: 'platform',
        header: col('platform'),
        columnType: 'text',
        sortable: false,
        widthMode: 'fit',
        size: 95,
        minSize: 85,
        meta: { className: 'min-w-[85px]' },
      },
      {
        id: 'arch',
        accessorKey: 'arch',
        header: col('arch'),
        columnType: 'text',
        sortable: false,
        cell: ({ value }) => {
          if (value == null || value === '') return 'any';
          if (typeof value === 'string' || typeof value === 'number') return String(value);
          return '—';
        },
        widthMode: 'fit',
        size: 70,
        minSize: 60,
        meta: { className: 'min-w-[60px]' },
      },
      {
        id: 'fileName',
        accessorKey: 'fileName',
        header: col('file'),
        columnType: 'text',
        sortable: false,
        widthMode: 'fit',
        size: 170,
        minSize: 130,
        cell: ({ value }) => {
          const fileName =
            typeof value === 'string' || typeof value === 'number' ? String(value) : '';
          return (
            <div className="max-w-[11rem] truncate" title={fileName}>
              {fileName || '—'}
            </div>
          );
        },
        meta: { className: 'min-w-[130px]' },
      },
      {
        id: 'sizeBytes',
        accessorKey: 'sizeBytes',
        header: col('size'),
        columnType: 'text',
        sortable: false,
        cell: ({ value }) => {
          let asNumber: number | null = null;
          if (typeof value === 'number') {
            asNumber = value;
          } else if (value != null) {
            asNumber = Number(value);
          }
          return formatBytes(asNumber);
        },
        widthMode: 'fit',
        size: 86,
        minSize: 74,
        meta: { className: 'min-w-[74px]' },
      },
      {
        id: 'killList',
        accessorKey: 'killList',
        header: col('kill_list'),
        columnType: 'text',
        sortable: false,
        cell: ({ value }) => {
          if (Array.isArray(value) && value.length > 0) {
            return (
              <div className="max-w-[8rem] truncate" title={value.join(', ')}>
                {value.join(', ')}
              </div>
            );
          }
          return '—';
        },
        widthMode: 'fit',
        size: 110,
        minSize: 90,
        meta: { className: 'min-w-[90px]' },
      },
      {
        id: 'publishedAt',
        accessorKey: 'publishedAt',
        header: col('published'),
        columnType: 'text',
        sortable: true,
        cell: ({ value }) => {
          if (!value) return 'Draft';
          try {
            return new Date(value as string).toLocaleString();
          } catch {
            if (typeof value === 'string' || typeof value === 'number') return String(value);
            return '—';
          }
        },
        widthMode: 'fit',
        size: 145,
        minSize: 120,
        meta: { className: 'min-w-[120px]' },
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: col('status'),
        columnType: 'badge',
        sortable: false,
        displayLabelOptions: statusOptions,
        widthMode: 'fit',
        size: 90,
        minSize: 80,
        meta: { className: 'min-w-[80px]' },
      },
      {
        id: 'isMandatory',
        accessorKey: 'isMandatory',
        header: col('update'),
        columnType: 'badge',
        sortable: false,
        displayLabelOptions: [
          { value: 'true', label: 'Mandatory' },
          { value: 'false', label: 'Optional' },
        ],
        widthMode: 'fit',
        size: 105,
        minSize: 90,
        meta: { className: 'min-w-[90px]' },
      },
      {
        id: 'actions',
        header: col('actions'),
        columnType: 'custom',
        sortable: false,
        cell: ({ row }) => (
          <AdminTableRowActions
            act={act}
            compact
            onView={() => setFormModal({ open: true, mode: 'view', release: row })}
            onEdit={() => setFormModal({ open: true, mode: 'edit', release: row })}
            extra={makeDesktopReleaseActionsCellRenderer({
              isCopyPending: downloadUrlMutation.isPending,
              isPublishPending: publishMutation.isPending,
              onCopy: handleCopy,
              onPublish: handlePublish,
            })({ row, value: null, index: 0 })}
          />
        ),
        widthMode: 'fit',
        size: 300,
        minSize: 230,
        meta: { className: 'min-w-[230px] text-right' },
      },
    ],

    [
      act,
      col,
      downloadUrlMutation.isPending,
      pagination.pageIndex,
      pagination.pageSize,
      publishMutation.isPending,
      statusOptions,
    ],
  );

  const releases = query.data ?? [];
  const filteredRows = useMemo(() => {
    let list = releases;
    const term = search.trim().toLowerCase();
    if (term) {
      list = list.filter(item => {
        const haystack = [
          item.appSlug,
          item.version,
          item.platform,
          item.arch ?? '',
          item.fileName,
          item.releaseNotes ?? '',
          ...(item.killList ?? []),
        ]
          .join(' ')
          .toLowerCase();
        return haystack.includes(term);
      });
    }
    if (statusFilter) {
      list = list.filter(item =>
        statusFilter === 'active' ? item.publishedAt != null : item.publishedAt == null,
      );
    }
    return list;
  }, [releases, search, statusFilter]);

  const sortedRows = useMemo(() => {
    if (!sorting.length) return filteredRows;
    const { id, desc } = sorting[0]!;
    return [...filteredRows].sort((a, b) => {
      const getDateValue = (value: string | null | undefined) =>
        value ? new Date(value).getTime() : Number.NEGATIVE_INFINITY;
      const cmp =
        id === 'publishedAt'
          ? getDateValue(a.publishedAt) - getDateValue(b.publishedAt)
          : (a.version ?? '').localeCompare(b.version ?? '');
      return desc ? -cmp : cmp;
    });
  }, [filteredRows, sorting]);

  const totalRecords = sortedRows.length;
  const pageSize = pagination.pageSize;
  const pageCount = Math.max(1, Math.ceil(Math.max(1, totalRecords) / pageSize));
  const safePageIndex = Math.min(pagination.pageIndex, pageCount - 1);
  const pageState: PaginationState = { pageIndex: safePageIndex, pageSize };
  const paginatedRows = useMemo(() => {
    const start = safePageIndex * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, safePageIndex, pageSize]);
  const controllers: DynamicTableControllers = {
    sort: {
      state: sorting,
      onSortingChange: updater => {
        setSorting(prev => (typeof updater === 'function' ? updater(prev) : updater));
        setPagination(prev => ({ ...prev, pageIndex: 0 }));
      },
    },
    pagination: {
      state: pageState,
      pageCount,
      rowCount: totalRecords,
      onPaginationChange: updater => {
        setPagination(prev => (typeof updater === 'function' ? updater(prev) : updater));
      },
    },
  };

  return (
    <div className="space-y-2 p-2">
      <div>
        <h1 className="text-3xl font-bold">{page('desktop_releases', 'title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{page('desktop_releases', 'subtitle')}</p>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div />
        <PagePrimaryActionButton
          onClick={() => setFormModal({ open: true, mode: 'create', release: null })}
          className="shadow-sm"
        >
          {page('desktop_releases', 'new')}
        </PagePrimaryActionButton>
      </div>

      <div className="glass-card overflow-hidden">
        <div className="flex flex-wrap items-end gap-2 border-b border-border p-3">
          <SearchFilter
            id="desktop-releases-search"
            label="Search"
            value={search}
            onChange={v => {
              setSearch(v);
              setPagination(prev => ({ ...prev, pageIndex: 0 }));
            }}
            placeholder="Search by app, version, platform, file..."
            className="[&_input]:h-9 [&_input]:py-1.5 [&_input]:text-sm"
          />
          <StatusFilter
            id="desktop-releases-status"
            label="Status"
            value={statusFilter}
            onChange={v => {
              setStatusFilter(v as DesktopReleaseStatusFilter);
              setPagination(prev => ({ ...prev, pageIndex: 0 }));
            }}
            options={statusOptions}
            emptyOption={allStatusesOption}
            className="[&_button]:h-9 [&_input]:h-9 [&_input]:py-1.5 [&_input]:text-sm"
          />
        </div>
        <DynamicTable<DesktopReleaseItem>
          features={{
            columns: true,
            rows: true,
            sorting: true,
            pagination: true,
            stripedRows: true,
            fontSize: 'xs',
            compactPadding: true,
          }}
          columns={columns}
          rows={{ data: paginatedRows, getRowId: row => String(row.id ?? '') }}
          controllers={controllers}
          isLoading={query.isLoading}
          loadingMessage={page('desktop_releases', 'loading')}
          emptyMessage={page('desktop_releases', 'empty')}
        />
      </div>

      <DesktopReleaseFormModal
        open={formModal.open}
        onClose={() =>
          setFormModal(prev => ({ ...prev, open: false, release: null }))
        }
        mode={formModal.mode}
        release={formModal.release}
        onSuccess={() => {
          void queryClient.invalidateQueries({ queryKey: desktopReleasesQueryKeys.all });
          setFormModal(prev => ({ ...prev, open: false, release: null }));
        }}
      />
    </div>
  );
}

